import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCheckLimitStore, useAccountsStore, useCardsStore, useDictionariesStore, useNotificationStore, useTopUpStore } from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType, type GetCardsResponse } from '@shared/api';
import { ApprovePasswordForm } from './approvePasswordForm';
import { Button, Constants, ErrorMessage, Input, ItemsType, Modal, Select, Spinner } from '@shared/widgets';

enum Step {
  Payment = 'payment',
  Password = 'password',
}

interface IForm {
  debitAccount?: ItemsType;
  toCard?: ItemsType;
  amount?: number;
  currency?: ItemsType;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, toCard: undefined, amount: undefined, currency: undefined };

interface TransferToCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCard?: GetCardsResponse;
}

export const TransferToCardModal = (props: TransferToCardModalProps) => {
  const accountStore = useAccountsStore();
  const cardStore = useCardsStore();
  const dictionariesStore = useDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const topUpStore = useTopUpStore();
  const notificationStore = useNotificationStore();

  const [step, setStep] = useState<Step>(Step.Payment);

  const { formState, control, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const toCard = watch('toCard');
  const amount = watch('amount');
  const currency = watch('currency');

  useEffect(() => {
    if (props.selectedCard) {
      setValue('toCard', { label: props.selectedCard.Number, leftLabel: props.selectedCard.Currency, value: props.selectedCard.Id });
      setValue('amount', props.selectedCard.InvoiceAmount > 0 ? props.selectedCard.InvoiceAmount : undefined);
    } else setValue('toCard', { label: cardOptions?.[0]?.label, leftLabel: cardOptions?.[0]?.leftLabel, value: cardOptions?.[0]?.value });
  }, [props.selectedCard, props.isOpen]);

  const cancelHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    if (Number(debitAccount?.label) < data.amount!) {
      notificationStore.setError(Constants.AMOUNT_ERROR);
      return;
    }

    const result = await checkLimitStore.checkLimit({
      requestType: RequestType.TransferToCard,
      accountIdFrom: data.debitAccount?.value,
      amount: data.amount,
      currency: data.currency?.value,
    });
    if (result.data?.LimitExceeded) {
      notificationStore.setError(result.data.Note);
      return;
    }

    const result2 = await topUpStore.createTopUp({
      cardId: data.toCard!.value,
      Account: data.debitAccount?.value,
      Amount: data.amount!,
      Currency: data.currency?.value,
    });
    if (result2) setStep(Step.Password);
  };
  const submitApprovePasswordHandler = async () => {
    props.onClose();
    setStep(Step.Payment);
    reset(DEFAULT_VALUES);
  };

  const currencyOptions = useMemo(() => {
    const result =
      dictionariesStore.data?.Currency.filter((item) => item.Name === debitAccount?.leftLabel || item.Name === toCard?.leftLabel).map((item) => ({
        value: item.Id,
        label: item.Name,
        leftLabel: item.Name,
      })) || [];
    if (result.length) setValue('currency', result[0]);
    return result;
  }, [dictionariesStore.data, debitAccount, toCard]);
  const cardOptions = useMemo(
    () => cardStore.data.map((item) => ({ value: item.Id, label: item.Number, leftLabel: item.Currency })),
    [cardStore.data],
  );
  const accountOptions = useMemo(
    () => accountStore.data.map((item) => ({ value: item.Id, label: item.Amount, leftLabel: item.Currency })),
    [accountStore.data],
  );
  return (
    <Modal isOpen={props.isOpen} title='Transfer to Card'>
      <Spinner show={formState.isSubmitting} fullscreen />
      {step === Step.Payment && (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              control={control}
              defaultValue={accountOptions?.[0]}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select items={accountOptions} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='toCard'
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>To Card</div>
                  <Select items={cardOptions} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field, fieldState: { error } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    {...field}
                    className='h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    required
                    value={field.value}
                    onChange={(value) => AmountUtils.amountChange(value, field.onChange)}
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            <Controller
              name='currency'
              control={control}
              defaultValue={currencyOptions?.[0]}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                  <Select items={currencyOptions} value={value?.value} onChange={onChange} />
                </div>
              )}
            />
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <Button className='h-[46px]' color='outlined' fullWidth onClick={cancelHandler}>
              Cancel
            </Button>
            <Button className='h-[46px]' fullWidth type='submit' disabled={!formState.isValid || formState.isSubmitting}>
              Continue
            </Button>
          </div>
        </form>
      )}
      {step === Step.Password && (
        <ApprovePasswordForm
          data={[
            { title: 'Debit Account:', label: debitAccount?.leftLabel, value: AmountUtils.transformToAmount(debitAccount?.label) },
            { title: 'To Card:', label: toCard?.leftLabel, value: toCard?.label },
            { title: 'Amount:', label: currency?.leftLabel, value: AmountUtils.transformToAmount(amount) },
          ]}
          requestId={topUpStore.data?.Id}
          onBack={() => setStep(Step.Payment)}
          onSubmit={submitApprovePasswordHandler}
        />
      )}
    </Modal>
  );
};
